import React from 'react';
import { Stack, Box, Typography, useTheme } from '@mui/material';

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  count?: number;
  gradient?: {
    from: string;
    to: string;
  };
  borderGradient?: {
    from: string;
    to: string;
  };
  textColor?: string;
}

export default function SectionHeader({
  title,
  subtitle,
  icon,
  action,
  count,
  gradient = { from: '#DBF6E1', to: '#CEEFF0' },
  borderGradient = { from: '#4AD16F', to: '#08B1B6' },
  textColor = '#022324',
}: SectionHeaderProps) {
  const theme = useTheme();
  
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      spacing={2}
      sx={{
        borderRadius: '8px 8px 0 0',
        background: `linear-gradient(180deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
        color: textColor,
        borderBottom: '2px solid transparent',
        borderImage: `linear-gradient(90deg, ${borderGradient.from} 0%, ${borderGradient.to} 100%) 1`,
        padding: theme.spacing(2),
        gap: theme.spacing(2),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${borderGradient.from} 0%, ${borderGradient.to} 100%)`,
        }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              padding: 1,
            }}
          >
            {React.cloneElement(icon as React.ReactElement, {
              sx: { color: textColor, fontSize: '1.5rem' }
            })}
          </Box>
        )}

        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            color={textColor}
            sx={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1.3,
              gap: 1.5,
            }}
          >
            {title}
            {typeof count === "number" && (
              <Box
                component="span"
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 12,
                  color: textColor,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {count} {count === 1 ? 'item' : 'items'}
              </Box>
            )}
          </Typography>

          {subtitle && (
            <Typography
              variant="subtitle2"
              sx={{
                color: textColor,
                opacity: 0.8,
                mt: 0.5,
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {action && (
        <Box sx={{
          mt: { xs: 1, sm: 0 },
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '& .MuiButton-root': {
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
          }
        }}>
          {action}
        </Box>
      )}
    </Stack>
  );
}